import React, { memo, useEffect, useMemo, useRef, useState } from "react";
import {
  Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography,
} from "@mui/material";
import UploadIcon from "@mui/icons-material/Upload";
import PrintIcon from "@mui/icons-material/Print";
import { toast } from "react-toastify";
import { uploadConsentForm } from "../../api/consentFormService";
import { useConsentForm } from "../../context/ConsentFormContext";
import { renderConsentForm } from "../../utils/buildConsentHtml";
import { readFileAsBase64 } from "../../utils/readFileAsBase64";

const ALLOWED_MIME_TYPES = new Set(["application/pdf", "image/jpeg", "image/png"]);
const ALLOWED_EXTENSIONS = [".pdf", ".jpg", ".jpeg", ".png"];

function isAllowedConsentFile(file) {
  if (!file) return false;
  const mime = String(file.type || "").toLowerCase();
  const name = String(file.name || "").toLowerCase();
  if (ALLOWED_MIME_TYPES.has(mime)) return true;
  return ALLOWED_EXTENSIONS.some((ext) => name.endsWith(ext));
}

function ConsentFormDialog({
  open,
  onClose,
  patient,
  patientId,
  updateField,
}) {
  const { template } = useConsentForm();
  const uploadInputRef = useRef(null);
  const previewUrlRef = useRef(null);

  const [uploadPreviewOpen, setUploadPreviewOpen] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const html = useMemo(
    () => renderConsentForm(template, patient),
    [template, patient]
  );

  const canUploadSignedCopy = Boolean(patientId || patient.patient_id || patient.uhid);

  const clearPreviewUrl = () => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
  };

  const resetUploadState = () => {
    clearPreviewUrl();
    setUploadedFile(null);
    setUploadPreviewOpen(false);
    if (uploadInputRef.current) {
      uploadInputRef.current.value = "";
    }
  };

  useEffect(() => () => clearPreviewUrl(), []);

  useEffect(() => {
    if (!open) {
      clearPreviewUrl();
      setUploadedFile(null);
      setUploadPreviewOpen(false);
      if (uploadInputRef.current) {
        uploadInputRef.current.value = "";
      }
    }
  }, [open]);

  const print = () => {
    const w = window.open("", "_blank", "width=900,height=1000");
    if (!w) return toast.error("Popup blocked");
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 250);
  };

  const handleUploadSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!isAllowedConsentFile(file)) {
      toast.error("Please upload a PDF, JPG, JPEG, or PNG file.");
      event.target.value = "";
      return;
    }

    try {
      const base64 = await readFileAsBase64(file);
      clearPreviewUrl();
      const previewUrl = URL.createObjectURL(file);
      previewUrlRef.current = previewUrl;

      setUploadedFile({
        name: file.name,
        base64,
        mimeType: file.type || "application/octet-stream",
        previewUrl,
        isPdf: file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf"),
      });
      setUploadPreviewOpen(true);
    } catch (err) {
      toast.error(err.message || "Failed to read uploaded file");
    } finally {
      event.target.value = "";
    }
  };

  const handleReupload = () => {
    resetUploadState();
    uploadInputRef.current?.click();
  };

  const handleSubmitUpload = async () => {
    const resolvedPatientId = patientId || patient.patient_id || patient.uhid;
    if (!resolvedPatientId) {
      toast.error("Patient ID is required before uploading consent form");
      return;
    }
    if (!uploadedFile?.base64) {
      toast.error("No uploaded file found");
      return;
    }

    setUploading(true);
    try {
      const result = await uploadConsentForm({
        patientId: resolvedPatientId,
        consoleForm: uploadedFile.base64,
      });
      updateField("consentFileName", uploadedFile.name);
      toast.success(result?.message || "Consent form uploaded successfully");
      resetUploadState();
    } catch (err) {
      toast.error(err.message || "Consent form upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>Consent Form</DialogTitle>
        <DialogContent>
          <Box
            sx={{ maxHeight: "60vh", overflow: "auto", border: 1, borderColor: "divider", borderRadius: 1, p: 2 }}
            dangerouslySetInnerHTML={{ __html: html }}
          />
          {patient.consentFileName && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
              Uploaded: {patient.consentFileName}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ flexWrap: "wrap", gap: 1 }}>
          {canUploadSignedCopy && (
            <Button component="label" variant="outlined" startIcon={<UploadIcon />}>
              Upload Signed Copy
              <input
                ref={uploadInputRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
                hidden
                onChange={handleUploadSelect}
              />
            </Button>
          )}
          <Button onClick={onClose}>Close</Button>
          <Button variant="contained" startIcon={<PrintIcon />} onClick={print}>Print</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={uploadPreviewOpen} onClose={resetUploadState} maxWidth="md" fullWidth>
        <DialogTitle>Review Uploaded Consent</DialogTitle>
        <DialogContent>
          {uploadedFile && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {uploadedFile.name}
              </Typography>
              {uploadedFile.isPdf ? (
                <Box
                  component="iframe"
                  src={uploadedFile.previewUrl}
                  title="Uploaded consent preview"
                  sx={{ width: "100%", height: 480, border: 1, borderColor: "divider", borderRadius: 1 }}
                />
              ) : (
                <Box
                  component="img"
                  src={uploadedFile.previewUrl}
                  alt="Uploaded consent preview"
                  sx={{
                    width: "100%",
                    maxHeight: 480,
                    objectFit: "contain",
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    bgcolor: "background.default",
                  }}
                />
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ flexWrap: "wrap", gap: 1 }}>
          <Button onClick={resetUploadState} disabled={uploading}>Cancel</Button>
          <Button variant="outlined" onClick={handleReupload} disabled={uploading}>
            Reupload
          </Button>
          <Button variant="contained" onClick={handleSubmitUpload} disabled={uploading}>
            {uploading ? <CircularProgress size={20} color="inherit" /> : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default memo(ConsentFormDialog);
